import { AGENCY_INFO } from '../data/content';
import { ArrowRight } from 'lucide-react';
import BrandLogo from './BrandLogo';

interface HeroProps {
  onOpenDiagnostic: () => void;
}

export default function Hero({ onOpenDiagnostic }: HeroProps) {
  return (
    <section id="hero-section" className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-[#0d2230] text-white">
      {/* Subtle grid pattern background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />

      {/* Ambient Radial Glow */}
      <div className="absolute -top-20 right-1/4 w-[600px] h-[600px] bg-cyan-500/10 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-[500px] h-[500px] bg-purple-500/10 blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Headline & Subtitle */}
          <div className="lg:col-span-7 space-y-6 text-left">
            {/* Tag Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900/90 border border-slate-700/60 text-slate-300 text-xs font-semibold tracking-wider uppercase">
              <span>CONSULTORA DE COMUNICACIONES</span>
              <div className="flex items-center gap-1 ml-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="w-1.5 h-1.5 rounded-full bg-pink-400" />
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.15] break-words">
              Construimos <br className="hidden sm:inline" />
              historias que <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 via-pink-400 to-purple-400">
                generan influencia
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-lg text-slate-300 max-w-xl font-normal leading-relaxed">
              Somos ÉPICA, una consultora de comunicación estratégica y posicionamiento digital. Transformamos mensajes en autoridad, reputación e influencia real.
            </p>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <button
                onClick={onOpenDiagnostic}
                id="hero-primary-cta"
                className="flex items-center justify-center gap-2 px-6 sm:px-7 py-3.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs sm:text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-95"
              >
                <span>Agendar reunión estratégica</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <a
                href="#servicios"
                id="hero-secondary-cta"
                className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-700/80 text-slate-200 font-bold text-xs sm:text-sm transition-colors"
              >
                <span>Ver servicios</span>
              </a>
            </div>

            {/* Key Metrics / Highlights */}
            <div className="pt-6 sm:pt-8 border-t border-slate-800/80 grid grid-cols-3 gap-2 sm:gap-4 max-w-lg">
              <div>
                <p className="text-xl sm:text-3xl font-black text-cyan-400">+14</p>
                <p className="text-[9px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider sm:tracking-widest mt-0.5">SECTORES</p>
              </div>
              <div>
                <p className="text-xl sm:text-3xl font-black text-emerald-400">360°</p>
                <p className="text-[9px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider sm:tracking-widest mt-0.5">ESTRATEGIA</p>
              </div>
              <div>
                <p className="text-xl sm:text-3xl font-black text-pink-400">IA</p>
                <p className="text-[9px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider sm:tracking-widest mt-0.5">APLICADA</p>
              </div>
            </div>
          </div>

          {/* Right Column: Directors Image Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden bg-slate-900/90 border border-slate-800/80 shadow-2xl p-2.5">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src={AGENCY_INFO.foundersImg}
                  alt="Angélica Baeza y Alejandro Osorio - Directores de ÉPICA Consultora de Comunicaciones"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />

                {/* Overlaid Label Card */}
                <div className="absolute bottom-4 left-4 right-4 p-3 rounded-2xl bg-slate-950/95 border border-slate-800/80 text-white shadow-xl flex items-center justify-between">
                  <BrandLogo size="sm" textColor="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
